package com.eladfinish.bible.contest.screens.mikud.usecases

import com.eladfinish.bible.contest.screens.mikud.api.SyllabusGithubApi
import com.eladfinish.bible.contest.screens.mikud.model.MikudLevelInfo
import com.eladfinish.bible.contest.screens.mikud.model.MikudType

/**
 * Fetches the youth/adults syllabus from the data repo on GitHub (same 3-file
 * intro/part1/part2 shape as the local assets). Returns null on any failure -- a network error,
 * a server error, an empty response, or any file missing -- so the caller can fall back to the
 * bundled local asset ([GetMikudLevelInfoUseCase]).
 */
class GetRemoteMikudLevelInfoUseCase(
    private val syllabusGithubApi: SyllabusGithubApi,
) {

    private data class Paths(val intro: String, val part1: String, val part2: String)

    suspend operator fun invoke(mikudType: MikudType): MikudLevelInfo? {
        val paths = when (mikudType) {
            MikudType.YOUTH -> Paths(
                intro = "youth/introduction_youth.txt",
                part1 = "youth/youth_state.txt",
                part2 = "youth/youth_religious.txt",
            )

            MikudType.ADULTS -> Paths(
                intro = "adults/introduction_adults.txt",
                part1 = "adults/adults_national.txt",
                part2 = "adults/adults_international.txt",
            )

            else -> return null
        }

        val intro = fetch(paths.intro, withSpacing = true) ?: return null
        val part1 = fetch(paths.part1, withSpacing = true) ?: return null
        val part2 = fetch(paths.part2, withSpacing = false) ?: return null

        return MikudLevelInfo(intro = intro, part1 = part1, part2 = part2)
    }

    private suspend fun fetch(relativePath: String, withSpacing: Boolean): String? {
        val text = syllabusGithubApi.getSyllabusFile(relativePath).dataOrNull()?.trim()
        if (text.isNullOrBlank()) return null
        return if (withSpacing) "$text\n\n" else text
    }
}
